import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { Box, Button, Divider, Switch, FormControlLabel, useTheme, Typography, Tabs, Tab } from '@mui/material';

import { useGetAllWorkflowsQuery, useGetStepsByManyWorkflowIds } from '@frontend/features/workflows/queries';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { ControlledAutocomplete } from '@frontend/features/common/components/ControlledAutocomplete';
import { ControlledCodeEditor } from '@frontend/features/common/components/ControlledCodeEditor';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';
import { client } from '@frontend/features/common/services';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';

import { CaseHandlerResponseDto } from '@bandido/shared/dtos';
import { OriginatedByChannel } from '@bandido/shared/types';
import { CaseStatus, CaseHandlerTools } from '@bandido/shared/constants';

import { useResolver, FormSchema, getPayload } from './helpers';

interface FormProps {
  onClose: () => void;
  agencyId: string;
  handler?: CaseHandlerResponseDto;
}

export const Form = ({ onClose, agencyId, handler }: FormProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { myProfile } = useAuth();
  const resolver = useResolver();
  const [activeTab, setActiveTab] = useState(0);

  const { data: scanWorkflows } = useGetAllWorkflowsQuery({
    pageSize: 1000,
    currentPage: 1,
    search: '',
    agencyId,
  });

  const methods = useForm<FormSchema>({
    resolver,
    defaultValues: {
      name: '',
      description: '',
      messageContextHistoryCount: 0,
      enabled: true,
      documentation: [],
      scanWorkflowId: undefined,
      channel: undefined,
      scanStepId: undefined,
      scanStatus: '',
      // Nuevos campos del IA handler
      handlerPurpose: '',
      behaviorRules: '',
      stepsDescription: '',
      handlerExample: '',
      conversationExample: '',
      validations: '',
      allowedTools: [],
      additionalNotes: '',
      nextWorkflowId: undefined,
      nextStepId: undefined,
      nextStatus: '',
      onCancelWorkflowId: undefined,
      onCancelStepId: undefined,
      onCancelStatus: '',
      onBailoutWorkflowId: undefined,
      onBailoutStepId: undefined,
      onBailoutStatus: '',
    },
  });
  const { control, handleSubmit, watch, setValue, reset } = methods;

  const enabledValue = watch('enabled');

  const scanWorkflowId = watch('scanWorkflowId');
  const nextWorkflowId = watch('nextWorkflowId');
  const onCancelWorkflowId = watch('onCancelWorkflowId');
  const onBailoutWorkflowId = watch('onBailoutWorkflowId');

  const scanWorkflowIds = scanWorkflowId ? [scanWorkflowId] : [];
  const nextWorkflowIds = nextWorkflowId ? [nextWorkflowId] : [];
  const onCancelWorkflowIds = onCancelWorkflowId ? [onCancelWorkflowId] : [];
  const onBailoutWorkflowIds = onBailoutWorkflowId ? [onBailoutWorkflowId] : [];

  const { data: scanSteps } = useGetStepsByManyWorkflowIds({
    workflowIds: scanWorkflowIds,
    search: '',
  });

  const { data: nextSteps } = useGetStepsByManyWorkflowIds({
    workflowIds: nextWorkflowIds,
    search: '',
  });

  const { data: onCancelSteps } = useGetStepsByManyWorkflowIds({
    workflowIds: onCancelWorkflowIds,
    search: '',
  });

  const { data: onBailoutSteps } = useGetStepsByManyWorkflowIds({
    workflowIds: onBailoutWorkflowIds,
    search: '',
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (handler) {
      setValue('scanStepId', handler.scanStepId);
    } else {
      setValue('scanStepId', undefined);
    }
  }, [scanWorkflowId, setValue, handler]);

  useEffect(() => {
    if (handler) {
      setValue('nextStepId', handler.nextStepId);
    } else {
      setValue('nextStepId', undefined);
    }
  }, [nextWorkflowId, setValue, handler]);

  useEffect(() => {
    if (handler) {
      setValue('onCancelStepId', handler.onCancelStepId);
    } else {
      setValue('onCancelStepId', undefined);
    }
  }, [onCancelWorkflowId, setValue, handler]);

  useEffect(() => {
    if (handler) {
      setValue('onBailoutStepId', handler.onBailoutStepId);
    } else {
      setValue('onBailoutStepId', undefined);
    }
  }, [onBailoutWorkflowId, setValue, handler]);

  useEffect(() => {
    if (handler) {
      reset(getPayload(handler));
    }
  }, [handler, reset]);

  const onSubmit = async (formData: FormSchema) => {
    try {
      if (handler) {
        await client.put(`/ia-case-event-handlers/${handler.id}`, {
          ...formData,
          agencyId: Number(agencyId),
          updatedBy: myProfile?.id,
        });
      } else {
        await client.post('/ia-case-event-handlers', {
          ...formData,
          agencyId: Number(agencyId),
          createdBy: myProfile?.id,
        });
      }

      addToast({
        message: t('caseHandlers.create.success'),
        color: 'success',
      });
      queryClient.invalidateQueries([QueryKeys.getCaseHandlers]);
      handleClose();
    } catch (e) {
      addToast({
        message: t('caseHandlers.create.error'),
        color: 'error',
      });
      console.log(e);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{
            paddingTop: theme.spacing(2),
          }}
        >

          <Box display="flex" gap={2} alignItems="center">
            <ControlledTextField
              control={control}
              name="name"
              size="small"
              label={t('caseHandlers.form.name')}
              placeholder={t('caseHandlers.form.namePlaceholder')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={enabledValue}
                  onChange={(e) => setValue('enabled', e.target.checked)}
                  color="primary"
                />
              }
              label={t('caseHandlers.form.enabled')}
              sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
            />
          </Box>



          <ControlledTextField
            control={control}
            name="description"
            size="small"
            label={t('caseHandlers.form.description')}
            placeholder={t('caseHandlers.form.descriptionPlaceholder')}
            fullWidth
            multiline
            rows={3}
          />

          <Divider />

          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={t('caseHandlers.form.tabs.listening')} />
            <Tab label={t('caseHandlers.form.tabs.handlePurpose')} />
            <Tab label={t('caseHandlers.form.tabs.behaviourRules')} />
            <Tab label={t('caseHandlers.form.tabs.steps')} />
            <Tab label={t('caseHandlers.form.tabs.resultExamples')} />
            <Tab label={t('caseHandlers.form.tabs.conversationExamples')} />
            <Tab label={t('caseHandlers.form.tabs.validations')} />
            <Tab label={t('caseHandlers.form.tabs.additionalNotes')} />
          </Tabs>

          {/* Carousel Container */}
          <Box sx={{ overflow: 'hidden', pt: 2, width: `calc(100vw - ${theme.spacing(4)})` }}>
            <Box 
              sx={{ 
                display: 'flex',
                transform: `translateX(calc(-${activeTab} * (100vw - ${theme.spacing(4)})))`,
                transition: 'transform 0.3s ease-in-out',
                width: `calc(8 * (100vw - ${theme.spacing(4)}))`, // 8 tabs * viewport width
                minHeight: '400px'
              }}
            >
              {/* Tab 0: Listening */}
              <Box sx={{ width: `calc(100vw - ${theme.spacing(4)})`, flexShrink: 0, px: 2, boxSizing: 'border-box' }}>
                <Box display="flex" gap={2} alignItems="center" justifyContent="center">
                  <ControlledAutocomplete
                    control={control}
                    name="channel"
                    size="small"
                    label={t('caseHandlers.form.channel')}
                    placeholder={t('caseHandlers.form.channelPlaceholder')}
                    options={Object.values(OriginatedByChannel)}
                    getOptionLabel={(option) => t(`cases.originatedByChannel.${option}`)}
                    fullWidth
                  />
                  <Box sx={{ width: '150px' }}>
                    <ControlledTextField
                      control={control}
                      name="messageContextHistoryCount"
                      label={t('caseHandlers.form.messageContextHistoryCount')}
                      placeholder="0"
                      type="number"
                      required
                      errorMessage={t('common.form.required')}
                      fullWidth
                    />
                  </Box>
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  <ControlledAutocomplete
                    control={control}
                    name="allowedTools"
                    size="small"
                    label={t('caseHandlers.form.allowedTools')}
                    placeholder={t('caseHandlers.form.allowedToolsPlaceholder')}
                    options={Object.values(CaseHandlerTools)}
                    getOptionLabel={(option) => option.replace(/_/g, ' ').toLowerCase()}
                    multiple
                    fullWidth
                  />
                </Box>
              
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2">{t('caseHandlers.form.listeningTo')}</Typography>
                
                <Box display="flex" gap={2} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                  <ControlledAutocomplete
                    control={control}
                    name="scanWorkflowId"
                    size="small"
                    label={t('caseHandlers.form.scanWorkflow')}
                    placeholder={t('caseHandlers.form.scanWorkflowPlaceholder')}
                    options={scanWorkflows?.items || []}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    fullWidth
                  />
                  <ControlledAutocomplete
                    size="small"
                    control={control}
                    name="scanStepId"
                    label={t('caseHandlers.form.scanStep')}
                    placeholder={t('caseHandlers.form.scanStepPlaceholder')}
                    options={scanSteps || []}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    disabled={!scanWorkflowId}
                    fullWidth
                  />
                  <ControlledAutocomplete
                    control={control}
                    name="scanStatus"
                    size="small"
                    label={t('caseHandlers.form.scanStatus')}
                    placeholder={t('caseHandlers.form.scanStatusPlaceholder')}
                    options={Object.values(CaseStatus)}
                    getOptionLabel={(option) => t(`cases.status.${option}`)}
                    fullWidth
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2">{t('caseHandlers.form.onSuccess')}</Typography>
                
                <Box display="flex" gap={2} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                  <ControlledAutocomplete
                    control={control}
                    name="nextWorkflowId"
                    size="small"
                    label={t('caseHandlers.form.nextWorkflow')}
                    placeholder={t('caseHandlers.form.nextWorkflowPlaceholder')}
                    options={scanWorkflows?.items || []}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    fullWidth
                  />
                  <ControlledAutocomplete
                    control={control}
                    name="nextStepId"
                    size="small"
                    label={t('caseHandlers.form.nextStep')}
                    placeholder={t('caseHandlers.form.nextStepPlaceholder')}
                    options={nextSteps || []}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    disabled={!nextWorkflowId}
                    fullWidth
                  />
                  <ControlledAutocomplete
                    control={control}
                    name="nextStatus"
                    size="small"
                    label={t('caseHandlers.form.nextStatus')}
                    placeholder={t('caseHandlers.form.nextStatusPlaceholder')}
                    options={Object.values(CaseStatus)}
                    getOptionLabel={(option) => t(`cases.status.${option}`)}
                    fullWidth
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant='subtitle2'>{t('caseHandlers.form.onBailout')}</Typography>
                
                <Box display="flex" gap={2} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                  <ControlledAutocomplete
                    control={control}
                    size="small"
                    name="onBailoutWorkflowId"
                    label={t('caseHandlers.form.nextWorkflow')}
                    placeholder={t('caseHandlers.form.nextWorkflowPlaceholder')}
                    options={scanWorkflows?.items || []}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    fullWidth
                  />
                  <ControlledAutocomplete
                    control={control}
                    size="small"
                    name="onBailoutStepId"
                    label={t('caseHandlers.form.nextStep')}
                    placeholder={t('caseHandlers.form.nextStepPlaceholder')}
                    options={onBailoutSteps || []}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    disabled={!onBailoutWorkflowId}
                    fullWidth
                  />
                  <ControlledAutocomplete
                    control={control}
                    name="onBailoutStatus"
                    size="small"
                    label={t('caseHandlers.form.nextStatus')}
                    placeholder={t('caseHandlers.form.nextStatusPlaceholder')}
                    options={Object.values(CaseStatus)}
                    getOptionLabel={(option) => t(`cases.status.${option}`)}
                    fullWidth
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant='subtitle2'>{t('caseHandlers.form.onCancel')}</Typography>
                
                <Box display="flex" gap={2} alignItems="center" justifyContent="center" sx={{ mt: 2 }}>
                  <ControlledAutocomplete
                    control={control}
                    size="small"
                    name="onCancelWorkflowId"
                    label={t('caseHandlers.form.nextWorkflow')}
                    placeholder={t('caseHandlers.form.nextWorkflowPlaceholder')}
                    options={scanWorkflows?.items || []}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    fullWidth
                  />
                  <ControlledAutocomplete
                    control={control}
                    name="onCancelStepId"
                    size="small"
                    label={t('caseHandlers.form.nextStep')}
                    placeholder={t('caseHandlers.form.nextStepPlaceholder')}
                    options={onCancelSteps || []}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    disabled={!onCancelWorkflowId}
                    fullWidth
                  />
                  <ControlledAutocomplete
                    control={control}
                    name="onCancelStatus"
                    size="small"
                    label={t('caseHandlers.form.nextStatus')}
                    placeholder={t('caseHandlers.form.nextStatusPlaceholder')}
                    options={Object.values(CaseStatus)}
                    getOptionLabel={(option) => t(`cases.status.${option}`)}
                    fullWidth
                  />
                </Box>
              </Box>

              {/* Tab 1: Handle Purpose */}
              <Box sx={{ width: `calc(100vw - ${theme.spacing(4)})`, flexShrink: 0, px: 2, boxSizing: 'border-box' }}>
                              <ControlledCodeEditor
                control={control}
                name="handlerPurpose"
                placeholder={t('caseHandlers.form.handlerPurposePlaceholder')}
                label={t('caseHandlers.form.handlerPurpose')}
                fullWidth
                rows={12}
              />
              </Box>

              {/* Tab 2: Behaviour Rules */}
              <Box sx={{ width: `calc(100vw - ${theme.spacing(4)})`, flexShrink: 0, px: 2, boxSizing: 'border-box' }}>
                              <ControlledCodeEditor
                control={control}
                name="behaviorRules"
                placeholder={t('caseHandlers.form.behaviorRulesPlaceholder')}
                label={t('caseHandlers.form.behaviorRules')}
                fullWidth
                rows={12}
              />
              </Box>

              {/* Tab 3: Steps */}
              <Box sx={{ width: `calc(100vw - ${theme.spacing(4)})`, flexShrink: 0, px: 2, boxSizing: 'border-box' }}>
                              <ControlledCodeEditor
                control={control}
                name="stepsDescription"
                placeholder={t('caseHandlers.form.stepsDescriptionPlaceholder')}
                label={t('caseHandlers.form.stepsDescription')}
                fullWidth
                rows={12}
              />
              </Box>

              {/* Tab 4: Result Examples */}
              <Box sx={{ width: `calc(100vw - ${theme.spacing(4)})`, flexShrink: 0, px: 2, boxSizing: 'border-box' }}>
                              <ControlledCodeEditor
                control={control}
                name="handlerExample"
                placeholder={t('caseHandlers.form.handlerExamplePlaceholder')}
                label={t('caseHandlers.form.handlerExample')}
                fullWidth
                rows={12}
              />
              </Box>

              {/* Tab 5: Conversation Examples */}
              <Box sx={{ width: `calc(100vw - ${theme.spacing(4)})`, flexShrink: 0, px: 2, boxSizing: 'border-box' }}>
                              <ControlledCodeEditor
                control={control}
                name="conversationExample"
                placeholder={t('caseHandlers.form.conversationExamplePlaceholder')}
                label={t('caseHandlers.form.conversationExample')}
                fullWidth
                rows={12}
              />
              </Box>

              {/* Tab 6: Validations */}
              <Box sx={{ width: `calc(100vw - ${theme.spacing(4)})`, flexShrink: 0, px: 2, boxSizing: 'border-box' }}>
                              <ControlledCodeEditor
                control={control}
                name="validations"
                placeholder={t('caseHandlers.form.validationsPlaceholder')}
                label={t('caseHandlers.form.validations')}
                fullWidth
                rows={8}
              />
                
                <Box sx={{ mt: 2 }}>
                  <ControlledAutocomplete
                    control={control}
                    name="allowedTools"
                    size="small"
                    label={t('caseHandlers.form.allowedTools')}
                    placeholder={t('caseHandlers.form.allowedToolsPlaceholder')}
                    options={Object.values(CaseHandlerTools)}
                    getOptionLabel={(option) => option.replace(/_/g, ' ').toLowerCase()}
                    multiple
                    fullWidth
                  />
                </Box>
              </Box>

              {/* Tab 7: Additional Notes */}
              <Box sx={{ width: `calc(100vw - ${theme.spacing(4)})`, flexShrink: 0, px: 2, boxSizing: 'border-box' }}>
                              <ControlledCodeEditor
                control={control}
                name="additionalNotes"
                placeholder={t('caseHandlers.form.additionalNotesPlaceholder')}
                label={t('caseHandlers.form.additionalNotes')}
                fullWidth
                rows={12}
              />
              </Box>

            </Box>
          </Box>



        </Box>

        <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-evenly"
            width="100%"
            gap={theme.spacing(1)}
            sx={{
              '& > button': {
                width: 'calc(50% - 4px)',
              },
            }}
          >
            <Button variant="outlined" color="error" size="medium" onClick={handleClose}>
              {t('common.form.discard')}
            </Button>
            <Button variant="contained" color="primary" type="submit" size="medium">
              {t('common.form.save')}
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider >
  );
};
